import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DateTimePicker } from '@material-ui/pickers';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import { withStyles } from '@material-ui/core/styles';
import Button from 'components/common/Button';
import Spin from 'components/common/Spin';
import Select from 'components/form/Select';
import SlotPicker from 'components/pages/calendar/SlotPicker';
import TIMEZONE, { DEFAULT_ZONE } from 'constant/timezones';
import moment from 'moment';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  paperFullWidth: {
    maxWidth: 700,
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h5">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    'padding': theme.spacing(3),
    'paddingTop': 0,
    '& .MuiPickersStaticWrapper-staticWrapperRoot': {
      padding: theme.spacing(1),
      borderRadius: 6,
      backgroundColor: 'rgba(248, 249, 252, 0.7)',
    },
    '& .MuiPickersCalendar-transitionContainer': {
      minHeight: 190,
    },
  },
}))(MuiDialogContent);

const ScheduleModal = ({
  onClose,
  onSubmit,
  title,
  classes,
  duration,
  allowCustomSlot,
  availablePeriods,
  studentId,
  userIds,
  children,
}) => {
  const user = useSelector((s) => s.user.user);
  const [saving, setSaving] = useState(false);
  const [showCustomSlot, toggleCustomSlot] = useState(false);
  const [customSlot, setCustomSlot] = useState(null);
  const [showTimezone, setShowTimezone] = useState(false);
  const [timezone, setTimezone] = useState(user.timezone || DEFAULT_ZONE);
  const timezoneLabel = TIMEZONE.find((t) => t.value === timezone).label;
  const [error, setError] = useState(false);

  const handleDateChange = (val) => {
    if (moment(val) < moment()) {
      setError(true);
    } else {
      setError(false);
    }
    setCustomSlot(val);
  };

  const handleSubmit = () => {
    const d = customSlot.tz(timezone, true);
    return onSubmit({
      start: d.toJSON().replace(/[.]\d+/, ''),
      end: d
        .add(duration, 'minutes')
        .toJSON()
        .replace(/[.]\d+/, ''),
    }).finally(() => setSaving(false));
  };

  const onPickSlot = ({ notification }) => {
    const { type, slot } = notification;
    if (type === 'slot_selected') {
      setSaving(true);
      onSubmit(slot).finally(() => setSaving(false));
    }
  };

  const toggleTimezone = (e) => {
    e.preventDefault();
    setShowTimezone(!showTimezone);
  };

  return (
    <Dialog
      onClose={onClose}
      fullWidth
      maxWidth={false}
      classes={{ paperFullWidth: classes.paperFullWidth }}
      open
    >
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <Spin spinning={saving}>
        <DialogContent>
          <Typography gutterBottom variant="caption" component="div">
            Select date and available time slot for video call, call timing is{' '}
            {duration} minutes.
          </Typography>
          {showTimezone ? (
            <div>
              <Select
                noMargin
                isMulti={false}
                options={TIMEZONE}
                transformValue
                value={timezone}
                onChange={setTimezone}
              />
            </div>
          ) : (
            <Typography gutterBottom variant="smallBold" color="textSecondary">
              Timezone: {timezoneLabel}{' '}
              <Link href="#" onClick={toggleTimezone}>
                Change
              </Link>
            </Typography>
          )}
          {children}
          <Box marginTop={4}>
            {!saving && showCustomSlot ? (
              <div>
                <DateTimePicker
                  disablePast
                  label="Select date & time"
                  inputVariant="filled"
                  value={customSlot}
                  onChange={handleDateChange}
                  fullWidth
                />
                <Typography variant="caption" color="secondary">
                  {error ? 'You selected past date/time.' : null}
                </Typography>
                <Box display="flex" justifyContent="flex-end" marginTop={4}>
                  <Button
                    onClick={() => toggleCustomSlot(false)}
                    style={{ marginRight: 8 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!customSlot || error}
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                  >
                    Save
                  </Button>
                </Box>
              </div>
            ) : (
              <>
                <SlotPicker
                  userIds={userIds}
                  availablePeriods={availablePeriods}
                  duration={duration}
                  onPick={onPickSlot}
                  timezone={timezone}
                />
                {allowCustomSlot && (
                  <Box textAlign="center" marginTop={3}>
                    <Button
                      color="primary"
                      onClick={() => toggleCustomSlot(true)}
                    >
                      Pick a custom slot
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
      </Spin>
    </Dialog>
  );
};

const WrappedScheduleModal = withStyles(styles)(ScheduleModal);

WrappedScheduleModal.propTypes = {
  onClose: PropTypes.func,
  availablePeriods: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }),
  duration: PropTypes.number,
  userIds: PropTypes.array,
  timezone: PropTypes.string,
  allowCustomSlot: PropTypes.bool,
};

export default WrappedScheduleModal;
